import { useMemo, useState } from "react";
import { CalendarOff, ChevronDown, ChevronRight, Plus, Scissors, Undo2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Input } from "@/components/ui/Input";
import {
  ORDER_CONFIRMATION_WINDOW_DAYS,
  PHASE_LABELS,
  addDaysISO,
  daysLabel,
  firstApiError,
  flattenApiErrors,
  formatDay,
  formatLeaveSpan,
  maxISO,
  useCancelContractorLeave,
  useContractorLeaves,
  useCreateContractorLeave,
  useShortenContractorLeave,
  type ContractorLeave,
  type LeavePhase,
} from "../api/leaves";

/**
 * Urlopy firmy podwykonawcy — jedna sekcja dla dwóch miejsc:
 *  - portal podwykonawcy, strona „Moje urlopy" (tylko konto firmy = konto z widocznością
 *    stawek; `canManage`),
 *  - karta firmy dla GK ELEKTIM (każdy wewnętrzny czyta; zapis tylko z uprawnieniem do
 *    podwykonawców — np. gdy firma zgłosi urlop telefonicznie).
 * Reguły zmian egzekwuje backend (can_cancel / can_shorten w wierszu); tu tylko nie
 * pokazujemy przycisków, których serwer by odrzucił, i zawężamy kalendarze.
 */

interface Props {
  contractorId: string;
  canManage: boolean;
  variant: "portal" | "card";
  /** Karta firmy: sekcja zwijana; portal: zawsze rozwinięta. */
  collapsible?: boolean;
}

const PHASE_COLOR: Record<LeavePhase, "orange" | "blue" | "gray"> = {
  ongoing: "orange",
  upcoming: "blue",
  past: "gray",
  cancelled: "gray",
};

export function ContractorLeavesSection({ contractorId, canManage, variant, collapsible }: Props) {
  const q = useContractorLeaves(contractorId);
  const [collapsed, setCollapsed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const leaves = q.data?.leaves ?? [];
  const today = q.data?.today;
  const current = useMemo(
    () => leaves.filter((l) => l.phase === "ongoing" || l.phase === "upcoming"),
    [leaves],
  );
  const history = useMemo(
    () => leaves.filter((l) => l.phase === "past" || l.phase === "cancelled"),
    [leaves],
  );

  const title = variant === "portal" ? "Zgłoszone urlopy" : "Urlopy firmy";
  const isCollapsible = collapsible ?? variant === "card";

  return (
    <section className="mb-6" data-testid="contractor-leaves-section">
      <div className="flex items-center justify-between gap-3 mb-3">
        {isCollapsible ? (
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            aria-expanded={!collapsed}
            className="flex items-center gap-1.5 text-base font-semibold text-gray-900 dark:text-gray-100 hover:text-[var(--color-primary)]"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            {title}
            {current.length > 0 && (
              <span className="ml-1 text-sm font-normal text-gray-500 dark:text-gray-400">
                ({current.length})
              </span>
            )}
          </button>
        ) : (
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        )}
        {canManage && !collapsed && !adding && (
          <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
            <Plus size={14} /> Zgłoś urlop
          </Button>
        )}
      </div>

      {collapsed ? null : (
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-5 space-y-4">
          {variant === "card" && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Dni, w których cała firma nie pracuje. Firma z urlopem w oknie składania zamówienia
              (dziś + {ORDER_CONFIRMATION_WINDOW_DAYS} dni na potwierdzenie) trafia na dół listy
              podwykonawców i podpowiedzi z dopiskiem „(urlop od–do)”. Zgłasza je w portalu konto firmy
              (konto z „Widocznością stawek”)
              {canManage ? " albo osoba z uprawnieniem do podwykonawców." : "."}
            </p>
          )}

          {adding && (
            <AddLeaveForm
              contractorId={contractorId}
              today={today}
              onDone={() => setAdding(false)}
            />
          )}

          {q.isLoading ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">Ładowanie…</p>
          ) : q.isError ? (
            <p className="text-sm text-red-600 dark:text-red-400">
              {firstApiError(q.error, "Nie udało się pobrać urlopów.")}
            </p>
          ) : current.length === 0 ? (
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <CalendarOff size={18} className="text-gray-300 dark:text-gray-600 shrink-0" />
              {variant === "portal"
                ? "Brak zaplanowanych urlopów — firma jest dostępna dla nowych zamówień."
                : "Brak zaplanowanych urlopów."}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {current.map((l) => (
                <LeaveRow
                  key={l.id}
                  leave={l}
                  today={today}
                  contractorId={contractorId}
                  canManage={canManage}
                />
              ))}
            </ul>
          )}

          {history.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 underline hover:text-[var(--color-primary)]"
              >
                {showHistory ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {showHistory ? "Ukryj minione i wycofane" : `Minione i wycofane (${history.length})`}
              </button>
              {showHistory && (
                <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-700 opacity-80">
                  {history.map((l) => (
                    <LeaveRow
                      key={l.id}
                      leave={l}
                      today={today}
                      contractorId={contractorId}
                      canManage={false}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ── formularz zgłoszenia ───────────────────────────────────────────────────────

function AddLeaveForm({
  contractorId,
  today,
  onDone,
}: {
  contractorId: string;
  today: string | undefined;
  onDone: () => void;
}) {
  const create = useCreateContractorLeave(contractorId);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = () => {
    const local: Record<string, string> = {};
    if (!start) local.start_date = "Podaj datę rozpoczęcia.";
    if (!end) local.end_date = "Podaj datę zakończenia.";
    if (start && end && end < start) local.end_date = "Data „do” nie może być wcześniejsza niż „od”.";
    setErrors(local);
    if (Object.keys(local).length) return;
    create.mutate(
      { start_date: start, end_date: end, note: note.trim() },
      {
        onSuccess: onDone,
        onError: (err) => {
          const flat = flattenApiErrors(err);
          setErrors(Object.keys(flat).length ? flat : { detail: "Nie udało się zgłosić urlopu." });
        },
      },
    );
  };

  return (
    <div
      data-testid="contractor-leave-form"
      className="rounded-lg border border-[var(--color-border)] p-3 space-y-3"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block text-xs text-gray-600 dark:text-gray-400">
          Od
          <DatePicker
            value={start}
            onChange={(v) => {
              setStart(v);
              if (v && end && end < v) setEnd(v);
            }}
            min={today}
            aria-label="Urlop od"
            className="mt-1"
          />
          {errors.start_date && <p className="mt-1 text-xs text-red-600">{errors.start_date}</p>}
        </label>
        <label className="block text-xs text-gray-600 dark:text-gray-400">
          Do
          <DatePicker
            value={end}
            onChange={setEnd}
            min={start || today}
            aria-label="Urlop do"
            className="mt-1"
          />
          {errors.end_date && <p className="mt-1 text-xs text-red-600">{errors.end_date}</p>}
        </label>
      </div>
      <label className="block text-xs text-gray-600 dark:text-gray-400">
        Uwagi (opcjonalnie)
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={255}
          placeholder="np. przerwa świąteczna, targi branżowe"
          className="mt-1"
        />
      </label>
      {errors.detail && <p className="text-xs text-red-600">{errors.detail}</p>}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} loading={create.isPending} disabled={create.isPending}>
          Zgłoś urlop
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone} disabled={create.isPending}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}

// ── wiersz urlopu ──────────────────────────────────────────────────────────────

function LeaveRow({
  leave,
  today,
  contractorId,
  canManage,
}: {
  leave: ContractorLeave;
  today: string | undefined;
  contractorId: string;
  canManage: boolean;
}) {
  const cancel = useCancelContractorLeave(contractorId);
  const [shortening, setShortening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCancel = () => {
    if (!confirm(`Wycofać urlop ${formatLeaveSpan(leave)}?`)) return;
    setError(null);
    cancel.mutate(leave.id, {
      onError: (err) => setError(firstApiError(err, "Nie udało się wycofać urlopu.")),
    });
  };

  return (
    <li className="py-3 first:pt-0 last:pb-0" data-testid="contractor-leave-row">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
              {formatLeaveSpan(leave)}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{daysLabel(leave.days)}</span>
            <Badge color={PHASE_COLOR[leave.phase]}>{PHASE_LABELS[leave.phase]}</Badge>
            {leave.original_end_date && leave.status === "active" && (
              <span
                className="text-xs text-gray-500 dark:text-gray-400"
                title="Urlop został skrócony"
              >
                skrócony z {formatDay(leave.original_end_date)}
              </span>
            )}
          </div>
          {leave.note && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">{leave.note}</p>
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            {leave.created_by_name ? `Zgłosił(a) ${leave.created_by_name}` : "Zgłoszono"}
            {" · "}
            {new Date(leave.created_at).toLocaleDateString("pl")}
            {leave.status === "cancelled" && leave.cancelled_by_name && (
              <>
                {" · wycofał(a) "}
                {leave.cancelled_by_name}
                {leave.cancelled_at ? ` ${new Date(leave.cancelled_at).toLocaleDateString("pl")}` : ""}
              </>
            )}
          </p>
          {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
        {canManage && (leave.can_shorten || leave.can_cancel) && !shortening && (
          <div className="flex items-center gap-2 shrink-0">
            {leave.can_shorten && (
              <Button size="sm" variant="ghost" onClick={() => setShortening(true)}>
                <Scissors size={13} /> Skróć
              </Button>
            )}
            {leave.can_cancel && (
              <Button
                size="sm"
                variant="danger"
                onClick={onCancel}
                loading={cancel.isPending}
                disabled={cancel.isPending}
              >
                <Undo2 size={13} /> Wycofaj
              </Button>
            )}
          </div>
        )}
      </div>
      {shortening && (
        <ShortenForm
          leave={leave}
          today={today}
          contractorId={contractorId}
          onDone={() => setShortening(false)}
        />
      )}
    </li>
  );
}

// ── skrócenie ──────────────────────────────────────────────────────────────────

function ShortenForm({
  leave,
  today,
  contractorId,
  onDone,
}: {
  leave: ContractorLeave;
  today: string | undefined;
  contractorId: string;
  onDone: () => void;
}) {
  const shorten = useShortenContractorLeave(contractorId);
  const ongoing = leave.phase === "ongoing";
  // Trwający: tylko koniec, w [max(start, wczoraj), stary koniec − 1] („wracam dziś" =
  // koniec wczoraj). Zaplanowany: start w [stary start, stary koniec], koniec w
  // [nowy start, stary koniec] — zakres ma się mieścić w starym.
  const yesterday = today ? addDaysISO(today, -1) : undefined;
  const endMin = ongoing && yesterday ? maxISO(leave.start_date, yesterday) : undefined;
  const endMax = ongoing ? addDaysISO(leave.end_date, -1) : leave.end_date;

  const [start, setStart] = useState(leave.start_date);
  const [end, setEnd] = useState(ongoing ? endMax : leave.end_date);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = () => {
    const payload: { id: string; start_date?: string; end_date?: string } = { id: leave.id };
    if (!ongoing && start !== leave.start_date) payload.start_date = start;
    if (end !== leave.end_date) payload.end_date = end;
    if (!payload.start_date && !payload.end_date) {
      setErrors({ detail: "Daty się nie zmieniły." });
      return;
    }
    setErrors({});
    shorten.mutate(payload, {
      onSuccess: onDone,
      onError: (err) => {
        const flat = flattenApiErrors(err);
        setErrors(Object.keys(flat).length ? flat : { detail: "Nie udało się skrócić urlopu." });
      },
    });
  };

  return (
    <div
      data-testid="contractor-leave-shorten-form"
      className="mt-2 rounded-lg border border-[var(--color-border)] p-3 space-y-3"
    >
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {ongoing
          ? "Urlop trwa — możesz oddać tylko przyszłe dni. Zakończenie „wczoraj” oznacza powrót do pracy od dziś."
          : "Urlop można tylko skrócić (późniejszy start i/lub wcześniejszy koniec). Wydłużenie zgłoś jako nowy urlop."}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block text-xs text-gray-600 dark:text-gray-400">
          Od
          <DatePicker
            value={start}
            onChange={(v) => {
              setStart(v);
              if (v && end < v) setEnd(v);
            }}
            min={leave.start_date}
            max={leave.end_date}
            disabled={ongoing}
            clearable={false}
            aria-label="Nowy początek urlopu"
            className="mt-1"
          />
          {errors.start_date && <p className="mt-1 text-xs text-red-600">{errors.start_date}</p>}
        </label>
        <label className="block text-xs text-gray-600 dark:text-gray-400">
          Do (ostatni dzień urlopu)
          <DatePicker
            value={end}
            onChange={setEnd}
            min={ongoing ? endMin : start || leave.start_date}
            max={endMax}
            clearable={false}
            aria-label="Nowy koniec urlopu"
            className="mt-1"
          />
          {errors.end_date && <p className="mt-1 text-xs text-red-600">{errors.end_date}</p>}
        </label>
      </div>
      {errors.detail && <p className="text-xs text-red-600">{errors.detail}</p>}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} loading={shorten.isPending} disabled={shorten.isPending}>
          Zapisz skrócenie
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone} disabled={shorten.isPending}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}
